/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import Grid from "@material-ui/core/Grid";
import Page from "components/common_components/Page/Page";
import Chat from "./Chat";
import ChatInitializerContext from "./ChatInitializerContext";
import AppContext from "AppContext";
import { hasElements } from "utils/arrays";
import { Form as FormikForm, AutocompleteField, TextField } from "components/common_components/Formik";
import { getAttributes } from "./utils";
import { stableSort } from "components/common_components/Tables/sortComparators";
import { CircularProgress } from "components/common_components/Progress";
import Header from "components/common_components/Header";
import Button from "components/common_components/Button";

const ChatOptions = {
    START_INTERNAL_CONVERSATION: "START_INTERNAL_CONVERSATION",
};

const ChatPage = () => {
    const dispatch = useDispatch();
    const [resources, setResources] = useState({ users: [] });
    const { user } = useSelector((state) => state.Login);
    const [activeOption, setActiveOption] = useState("");

    const {
        api: {
            Settings: { Users },
            Chat: { Conversations },
        },
        messaging,
        conversations,
        changeChatProfile,
    } = useContext(AppContext);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
            },
        });
    }

    useEffect(() => {
        getUsers();
        changeChatProfile(String(user.email).replace("@", "|_at_|"));

        return () => {
            setResources((prev) => ({
                users: [],
            }));
            changeChatProfile(undefined);
        };
    }, []);

    const authorLabelResolver = (message) => {
        const { author, attributes } = message;
        if (attributes && attributes.senderUserEmail) {
            const { senderUserEmail } = attributes;
            const authorUser = resources.users.find((u) => u.email === senderUserEmail);
            if (authorUser) {
                return `${String(authorUser.name).trim()}`;
            }
            return `Unknown User (${senderUserEmail})`;
        }
        return author;
    };

    const clientPhotoResolver = (conversation) => {
        const { type } = getAttributes(conversation);

        if (type === "internal_direct") {
            return require("assets/images/chat-user-direct.png");
            // TODO: Get the user's photo when implemented
        }

        if (type === "internal_group") {
            return require("assets/images/chat-users-group.png");
        }
    };

    const conversationNameResolver = async (conversation) => {
        const { type } = getAttributes(conversation);

        if (type === "internal_direct") {
            const participants = await conversation.getParticipants();
            const otherParticipant = participants.find((p) => p.identity !== messaging.profile);
            const parsedEmail = String(otherParticipant.identity).replace("|_at_|", "@");
            const otherParticipantUser = resources.users.find((u) => u.email === parsedEmail);
            return otherParticipantUser ? otherParticipantUser.name : parsedEmail;
        }
        return conversation.friendlyName;
    };

    const getInternalUsers = () => {
        if (hasElements(resources.users)) {
            return resources.users.filter((x) => x.id !== user.id);
        }
        return [];
    };

    const getInitialValues = () => {
        return { internalParticipants: [], friendlyName: "" };
    };

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        if (hasElements(values.internalParticipants)) {
            try {
                setSubmitting(true);
                const isGroup = values.internalParticipants.length > 1;
                const isDirect = values.internalParticipants.length === 1;
                const currentIdentity = messaging.profile;
                if (isGroup) {
                    if (values.friendlyName) {
                        const identities = values.internalParticipants.map((x) => x.email.replace("@", "|_at_|"));
                        await Conversations.createAsync({
                            friendlyName: values.friendlyName,
                            identities: [currentIdentity, ...identities],
                            attributes: {
                                type: "internal_group",
                                userIds: values.internalParticipants.map((x) => x.id),
                            },
                        });
                    } else {
                        setErrors({ friendlyName: "This field is required for groups!" });
                        setSubmitting(false);
                        return;
                    }
                } else if (isDirect) {
                    const usr = values.internalParticipants[0];
                    const identity = usr.email.replace("@", "|_at_|");
                    await Conversations.createAsync({
                        friendlyName: usr.name,
                        identities: [currentIdentity, identity],
                        attributes: {
                            type: "internal_direct",
                            userId: usr.id,
                        },
                    });
                }
                dispatch(notifySuccess("Conversation started!"));
                setActiveOption("");
            } catch (message) {
                dispatch(notifyError(`The conversation couldn't be started. \n Cause: \n ${message}`));
            } finally {
                setSubmitting(false);
            }
        }
    }

    const conversationsGetter = () => {
        if (hasElements(conversations)) {
            const internalConversations = conversations.filter((conversation) => {
                const { type } = getAttributes(conversation);
                return type && Boolean(type.startsWith("internal_"));
            });
            return hasElements(internalConversations) ? stableSort(internalConversations, "desc", "dateCreated") : [];
        }
        return [];
    };

    if (!hasElements(resources.users)) {
        return (
            <div className="chat-page-loading">
                <CircularProgress />
            </div>
        );
    }

    return (
        <Page>
            <Header title="Chat">
                <Button
                    primary
                    small
                    disabled={activeOption === ChatOptions.START_INTERNAL_CONVERSATION}
                    onClick={() => setActiveOption(ChatOptions.START_INTERNAL_CONVERSATION)} >
                    New internal conversation
                </Button>
            </Header>
            <Grid container spacing={2}>
                <Grid item xs={12} className="chat-option-content">
                    {activeOption === ChatOptions.START_INTERNAL_CONVERSATION && (
                        <div className="start-internal-conversation">
                            <FormikForm
                                id="start-internal-conversation-form"
                                onSubmit={onSubmitHandler}
                                validateOnChange={false}
                                initialValues={getInitialValues()}
                                onCancel={() => setActiveOption("")}
                            >
                                {(form) => (
                                    <>
                                        <AutocompleteField
                                            fieldName="internalParticipants"
                                            multiple
                                            limitTags={2}
                                            options={getInternalUsers()}
                                            label="Participants"
                                            valueKey="id"
                                            labelKey="name"
                                            variant="standard"
                                            renderOption={(option) => (
                                                <div className="autocomplete-option">
                                                    <div className="autocomplete-option-username">
                                                        <div>
                                                            <strong>{option.name}</strong>
                                                        </div>
                                                    </div>
                                                    <small>{option.email}</small>
                                                </div>
                                            )}
                                            getOptionLabel={(option) => {
                                                if (option) {
                                                    return option.name;
                                                }
                                                return "";
                                            }}
                                        />
                                        {form.values.internalParticipants.length > 1 && (
                                            <TextField
                                                fieldName="friendlyName"
                                                label="Group's name"
                                                variant="standard"
                                                size="medium"
                                                placeholder="Add a friendly name for the new group"
                                            />
                                        )}
                                    </>
                                )}
                            </FormikForm>
                        </div>
                    )}
                </Grid>
                <Grid item xs={12} className="chat-in-grid">
                    <ChatInitializerContext.Provider
                        value={{ authorLabelResolver, clientPhotoResolver, conversationsGetter, conversationNameResolver }}
                    >
                        <Chat />
                    </ChatInitializerContext.Provider>
                </Grid>
            </Grid>
        </Page >
    );
};

export default ChatPage;
